import React, { useEffect, useMemo, useState } from "react";
import { Grid, Card, CardContent, Typography, CircularProgress } from "@mui/material";
import Navbar from "./Navbar";
import Logger from "./Logger";
import { getLoggerBaseURL } from "../utils/getLoggerBaseURL";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";

const PackerDashboard = () => {
    const [orders, setOrders] = useState([]);
    const [summaries, setSummaries] = useState([]);
    const [printRequests, setPrintRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const adminEmail = sessionStorage.getItem("adminEmail");
    const navigate = useNavigate();
    const logger = useMemo(() => new Logger(getLoggerBaseURL()), []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(`/api/packer/getSummary/${adminEmail}`);

                if (!response.ok) {
                    throw new Error("Failed to fetch dashboard data");
                }

                const data = await response.json();

                setOrders(data.OrderNumbers || []);
                setSummaries(data.Summaries || []);

                const queueResponse = await fetch(`/api/DrOrder/all/getQueue/${adminEmail}?page=1&limit=5`);

                if (!queueResponse.ok) {
                    throw new Error("Failed to fetch print requests");
                }

                const queueData = await queueResponse.json();

                setPrintRequests(queueData.data || []);
            } catch (error) {
                logger.log(error.message, "ERROR");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [adminEmail, logger]);

    if (loading) {
        return (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh" }}            >
                <CircularProgress />
                <Typography sx={{ marginTop: 2 }}>
                    Fetching dashboard...
                </Typography>
            </div>
        );
    }

    return (
        <>
            <Navbar />
            <div style={{ background: "#f5f7fb", minHeight: "100vh", padding: "20px" }} >
                {/* Header */}
                <div style={{ marginBottom: "15px", marginTop: "50px" }} >
                    <Typography sx={{ fontWeight: "bold", fontSize: "25px", lineHeight: "40px" }} >
                        Dashboard
                    </Typography>
                </div>

                {loading ? (
                    <div style={{ height: "70vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: "12px" }} >
                        <CircularProgress size={28} />
                        <Typography sx={{ color: "#64748b", fontSize: "14px" }} >
                            Loading dashboard...
                        </Typography>
                    </div>
                ) : (
                    <Grid container spacing={3}>
                        {/* Orders Tile */}
                        <Grid item xs={12} md={4}>
                            <Card sx={{ borderRadius: "18px", border: "1px solid #e5e7eb", boxShadow: "0 2px 10px rgba(0,0,0,0.04)", height: "100%" }} >
                                <CardContent sx={{ padding: "24px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }} >
                                        <Typography sx={{ color: "#6b7280", fontSize: "15px", fontWeight: 500 }} >
                                            Pending Orders
                                        </Typography>

                                        <Button
                                            variant="contained"
                                            onClick={() => navigate("/packerOrders")}
                                            sx={{
                                                background: "#1976d2",
                                                textTransform: "none",
                                                borderRadius: "10px",
                                                boxShadow: "none",
                                                minWidth: "110px",
                                                "&:hover": {
                                                    background: "#1565c0",
                                                    boxShadow: "none",
                                                },
                                            }}
                                        >
                                            Show All
                                        </Button>
                                    </div>

                                    <Typography sx={{ fontSize: "38px", fontWeight: 700, color: "#1976d2" }} >
                                        {orders.length}
                                    </Typography>

                                    <div>
                                        {orders.slice(0, 5).map((order, index) => (
                                            <div
                                                key={index}
                                                style={{
                                                    padding: "10px 0",
                                                    borderBottom:
                                                        index !== 4
                                                            ? "1px solid #f3f4f6"
                                                            : "none",
                                                }}
                                            >
                                                <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#111827" }} >
                                                    {order.Order_No}
                                                </Typography>

                                                <Typography sx={{ fontSize: "13px", color: "#6b7280" }} >
                                                    {order.Comp_Name || "N/A"}
                                                </Typography>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Summaries Tile */}
                        <Grid item xs={12} md={4}>
                            <Card sx={{ borderRadius: "18px", border: "1px solid #e5e7eb", boxShadow: "0 2px 10px rgba(0,0,0,0.04)", height: "100%" }} >
                                <CardContent sx={{ padding: "24px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }} >
                                        <Typography sx={{ color: "#6b7280", fontSize: "15px", fontWeight: 500 }} >
                                            Pending Summaries
                                        </Typography>

                                        <Button
                                            variant="contained"
                                            onClick={() => navigate("/packerSummaries")}
                                            sx={{
                                                background: "#1a7f22",
                                                textTransform: "none",
                                                borderRadius: "10px",
                                                boxShadow: "none",
                                                minWidth: "110px",
                                                "&:hover": {
                                                    background: "#166534",
                                                    boxShadow: "none",
                                                },
                                            }}
                                        >
                                            Show All
                                        </Button>
                                    </div>

                                    <Typography sx={{ fontSize: "38px", fontWeight: 700, color: "#1a7f22", }} >
                                        {summaries.length}
                                    </Typography>

                                    <div>
                                        {summaries
                                            .slice(0, 5)
                                            .map((summary, index) => (
                                                <div
                                                    key={index}
                                                    style={{
                                                        padding: "10px 0",
                                                        borderBottom:
                                                            index !== 4
                                                                ? "1px solid #f3f4f6"
                                                                : "none",
                                                    }}
                                                >
                                                    <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#111827" }} >
                                                        {summary.Order_No}
                                                    </Typography>

                                                    <Typography sx={{ fontSize: "13px", color: "#6b7280" }} >
                                                        {summary.Comp_Name || "N/A"}
                                                    </Typography>
                                                </div>
                                            ))}
                                    </div>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Print Requests Tile */}
                        <Grid item xs={12} md={4}>
                            <Card sx={{ borderRadius: "18px", border: "1px solid #e5e7eb", boxShadow: "0 2px 10px rgba(0,0,0,0.04)", height: "100%" }} >
                                <CardContent sx={{ padding: "24px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }} >
                                        <Typography sx={{ color: "#6b7280", fontSize: "15px", fontWeight: 500 }} >
                                            Pending Print Requests
                                        </Typography>

                                        <Button
                                            variant="contained"
                                            onClick={() => navigate("/getQueue")}
                                            sx={{
                                                background: "#7c3aed",
                                                textTransform: "none",
                                                borderRadius: "10px",
                                                boxShadow: "none",
                                                minWidth: "110px",
                                                "&:hover": {
                                                    background: "#6d28d9",
                                                    boxShadow: "none",
                                                },
                                            }}
                                        >
                                            Show All
                                        </Button>
                                    </div>

                                    <Typography sx={{ fontSize: "38px", fontWeight: 700, color: "#7c3aed" }} >
                                        {printRequests.length}
                                    </Typography>

                                    <div>
                                        {printRequests
                                            .slice(0, 5)
                                            .map((request, index) => (
                                                <div
                                                    key={request._id || index}
                                                    style={{
                                                        padding: "10px 0",
                                                        borderBottom:
                                                            index !== 4
                                                                ? "1px solid #f3f4f6"
                                                                : "none",
                                                    }}
                                                >
                                                    <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#111827" }} >
                                                        {request.data?.Summary_No ||
                                                            request.data?.Order_No}
                                                    </Typography>

                                                    <Typography sx={{ fontSize: "13px", color: "#6b7280" }} >
                                                        Copies:{" "}
                                                        {request.data?.noOfCopies || 0}
                                                    </Typography>
                                                </div>
                                            ))}
                                    </div>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}
            </div>
        </>
    );
};

export default PackerDashboard;